import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import MovieCard from "@/components/base/MovieCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getHot, searchMovies } from "@/lib/movies";
import type { MovieType } from "@/types/movie";

type Filter = "all" | MovieType;

const filters: { key: Filter; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tv", label: "电视剧" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [input, setInput] = useState(query);
  const [filter, setFilter] = useState<Filter>("all");

  useEffect(() => {
    setInput(query);
  }, [query]);

  useDocumentMeta({
    title: query ? `「${query}」搜索结果 - 天天影院` : "影视搜索 - 天天影院",
    description: query
      ? `天天影院搜索「${query}」的相关影视作品，为你推荐匹配的电影与电视剧，包含剧情简介与影片推荐。`
      : "在天天影院搜索最新电影与电视剧，输入影视名称即可快速找到想看的内容。",
    keywords: `天天影院搜索,${query || "影视搜索"},电影搜索,电视剧搜索`,
  });

  const results = useMemo(() => searchMovies(query, filter), [query, filter]);
  const hotList = useMemo(() => getHot(5), []);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
  };

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-8 animate-fade-in">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "搜索" }]} />

      <div className="mt-5 rounded-lg bg-primary-950 px-6 py-7 md:px-8 md:py-9">
        <h1 className="font-heading text-xl md:text-3xl font-bold text-background-50 mb-4">
          搜索影视作品
        </h1>
        <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-2xl">
          <div className="relative flex-1">
            <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="输入影视名称、类型或演员，如「科幻」「迷雾之城」"
              className="w-full rounded-lg border border-background-300 bg-background-50 pl-11 pr-4 py-3 text-sm text-foreground-900 outline-none transition-colors focus:border-accent-400"
              aria-label="搜索影视"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-accent-500 hover:bg-accent-600 text-background-50 text-sm font-medium px-7 py-3 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-search-line" />
            搜索
          </button>
        </form>
        {query && (
          <p className="mt-4 text-sm text-background-200/90">
            当前关键词：<span className="text-accent-400 font-medium">{query}</span>
          </p>
        )}
      </div>

      {query ? (
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1fr_300px] gap-6">
          <section>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <p className="text-sm text-foreground-600">
                共找到 <span className="text-primary-600 font-semibold">{results.length}</span> 条结果
              </p>
              <div className="flex items-center gap-1 rounded-full bg-background-200 p-1">
                {filters.map((item) => (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => setFilter(item.key)}
                    className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      filter === item.key
                        ? "bg-primary-500 text-background-50 font-medium"
                        : "text-foreground-600 hover:text-primary-600"
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </div>

            {results.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-5">
                {results.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} showActors />
                ))}
              </div>
            ) : (
              <div className="rounded-lg border border-background-200 bg-background-50 py-14 text-center">
                <i className="ri-emotion-sad-line text-5xl text-foreground-300" />
                <p className="mt-4 text-foreground-700">
                  抱歉，没有找到与「{query}」相关的影视作品
                </p>
                <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或看看右侧的热门推荐</p>
              </div>
            )}
          </section>

          <aside>
            <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
              <i className="ri-fire-line text-accent-500" />
              热门影视推荐
            </h2>
            <div className="flex flex-col gap-3">
              {hotList.map((movie, index) => (
                <div key={movie.id} className="flex items-center gap-2">
                  <span className="w-6 shrink-0 text-center font-heading font-bold text-foreground-400">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <MovieCard movie={movie} variant="compact" showRating={false} />
                  </div>
                </div>
              ))}
            </div>
          </aside>
        </div>
      ) : (
        <section className="mt-8">
          <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-4 flex items-center gap-2">
            <i className="ri-fire-line text-accent-500" />
            大家都在看
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3 md:gap-5">
            {hotList.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}